import React, { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { api, formatApiError } from "../../lib/api";
import { Phone, Mail, MapPin, Instagram, Check } from "lucide-react";
import { toast } from "sonner";

const Contact = ({ info }) => {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_type: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact.form.success"));
      setForm({ name: "", email: "", phone: "", event_date: "", event_type: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast.error(formatApiError(err) || t("contact.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 focus:border-[#D4AF37] focus:outline-none text-white transition-colors placeholder:text-white/30";
  const labelCls = "text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-2 block";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#0A0A0C] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-6">
            {t("contact.overline")}
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter text-white leading-[1]">
            {t("contact.title")}
          </h2>
          <p className="mt-6 text-base text-white/60 leading-relaxed max-w-md">
            {t("contact.subtitle")}
          </p>

          <div className="mt-12 space-y-6">
            {info?.phones?.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/\s/g, "")}`}
                className="flex items-center gap-4 group"
                data-testid={`contact-phone-${p.replace(/\s|\+/g, "")}`}
              >
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                  <Phone size={14} className="text-[#D4AF37]" />
                </div>
                <span className="text-white/80 group-hover:text-white transition-colors">{p}</span>
              </a>
            ))}
            <a href={`mailto:${info?.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
              <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                <Mail size={14} className="text-[#D4AF37]" />
              </div>
              <span className="text-white/80 group-hover:text-white transition-colors">{info?.email}</span>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-address">
              <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center">
                <MapPin size={14} className="text-[#D4AF37]" />
              </div>
              <span className="text-white/80">{info?.address}</span>
            </div>
            {info?.instagram && (
              <a
                href={info.instagram}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram"
              >
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors">
                  <Instagram size={14} className="text-[#D4AF37]" />
                </div>
                <span className="text-white/80 group-hover:text-white transition-colors">
                  @istanbulorganizasyonfr
                </span>
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-7 bg-[#121217] border border-white/5 p-8 md:p-12">
          <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <label className={labelCls}>{t("contact.form.name")}</label>
                <input
                  type="text"
                  required
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className={labelCls}>{t("contact.form.email")}</label>
                <input
                  type="email"
                  required
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className={labelCls}>{t("contact.form.phone")}</label>
                <input
                  type="tel"
                  className={inputCls}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className={labelCls}>{t("contact.form.event_date")}</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.event_date}
                  onChange={(e) => update("event_date", e.target.value)}
                  data-testid="input-event-date"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t("contact.form.event_type")}</label>
              <select
                className={inputCls + " appearance-none cursor-pointer"}
                value={form.event_type}
                onChange={(e) => update("event_type", e.target.value)}
                data-testid="input-event-type"
              >
                <option value="" className="bg-[#121217]">—</option>
                <option value="wedding" className="bg-[#121217]">
                  {t("contact.form.event_options.wedding")}
                </option>
                <option value="engagement" className="bg-[#121217]">
                  {t("contact.form.event_options.engagement")}
                </option>
                <option value="henna" className="bg-[#121217]">
                  {t("contact.form.event_options.henna")}
                </option>
                <option value="other" className="bg-[#121217]">
                  {t("contact.form.event_options.other")}
                </option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("contact.form.message")}</label>
              <textarea
                required
                rows={4}
                className={inputCls + " resize-none"}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="input-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="w-full sm:w-auto bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#C5A059] disabled:opacity-50 px-12 py-4 uppercase tracking-[0.25em] text-xs font-bold transition-colors inline-flex items-center justify-center gap-3"
            >
              {sent ? (
                <>
                  <Check size={16} /> ✓
                </>
              ) : submitting ? (
                t("contact.form.sending")
              ) : (
                t("contact.form.submit")
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
